import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { forgotPassword } from '../api/auth';
import { toast } from '../components/common/Toast';
import SmsVerifyStep from '../components/SmsVerifyStep';
import { formatPhoneInput, isValidPhone } from '../utils/phone';
import './Pages.css';

const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<1 | 2 | 3>(1);

  const [phone, setPhone] = useState('');
  const [smsVerified, setSmsVerified] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleStep1Next = () => {
    if (!isValidPhone(phone)) {
      setError('올바른 전화번호 형식이 아닙니다 (예: 010-1234-5678)');
      return;
    }
    setError('');
    setStep(2);
  };

  const handleSmsVerified = () => {
    setSmsVerified(true);
    setStep(3);
  };

  const handleSubmit = async () => {
    if (!smsVerified) {
      setError('SMS 인증을 완료해주세요');
      return;
    }
    if (newPassword.length < 8) {
      setError('비밀번호는 8자 이상이어야 합니다');
      return;
    }
    if (newPassword !== newPasswordConfirm) {
      setError('비밀번호가 일치하지 않습니다');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await forgotPassword(phone, newPassword);
      sessionStorage.setItem('auth_redirect_message', '비밀번호가 재설정되었습니다. 새 비밀번호로 로그인해주세요');
      navigate('/login');
    } catch (err) {
      const msg = axios.isAxiosError(err)
        ? (err.response?.data as { error?: string } | undefined)?.error
        : undefined;
      toast.error(msg || '비밀번호 재설정에 실패했습니다');
      setError(msg || '비밀번호 재설정에 실패했습니다');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-form">
        <div className="login-logo">NUCODE</div>
        <h2 className="login-title">비밀번호 찾기</h2>

        {/* Step 1: 전화번호 입력 */}
        <div className="form-group">
          <label>전화번호</label>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(formatPhoneInput(e.target.value))}
            placeholder="010-0000-0000"
            maxLength={13}
            disabled={step > 1}
          />
        </div>
        {step === 1 && (
          <button
            type="button"
            className="btn btn--primary login-btn"
            onClick={handleStep1Next}
          >
            다음
          </button>
        )}

        {/* Step 2: SMS 인증 */}
        {step >= 2 && (
          <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #eee' }}>
            <label style={{ fontWeight: 600, display: 'block', marginBottom: 8 }}>SMS 인증</label>
            <SmsVerifyStep
              phone={phone}
              purpose="password_reset"
              onVerified={handleSmsVerified}
              disabled={smsVerified}
            />
          </div>
        )}

        {/* Step 3: 새 비밀번호 */}
        {step >= 3 && (
          <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #eee' }}>
            <div className="form-group">
              <label>새 비밀번호 (8자 이상, 대/소문자+숫자+특수)</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="새 비밀번호"
              />
            </div>
            <div className="form-group">
              <label>새 비밀번호 확인</label>
              <input
                type="password"
                value={newPasswordConfirm}
                onChange={(e) => setNewPasswordConfirm(e.target.value)}
                placeholder="비밀번호 확인"
              />
            </div>
            <button
              type="button"
              className="btn btn--primary login-btn"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? '재설정 중...' : '비밀번호 재설정'}
            </button>
          </div>
        )}

        {error && <p style={{ color: '#F44336', marginTop: 8, fontSize: 13 }}>{error}</p>}

        <p style={{ textAlign: 'center', marginTop: 16, fontSize: 14 }}>
          <Link to="/login">로그인으로 돌아가기</Link>
        </p>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
